import Box from '@mui/material/Box';
import { useState, ReactNode, SyntheticEvent, MouseEvent, useEffect } from 'react';
import { HistoricalInfo } from './info';
import { Text } from '@/components/atoms/text';
import {
  HistoricalInformationContainer,
  HistoricalInformationHeader,
  StyledTab,
  StyledTabs,
} from './styles';
import { LineChart } from '../charts/line';
import { Mobile } from '../viewport/mobile';
import { IconButton } from '@/components/atoms/Button';
import { Close } from '@mui/icons-material';
import type { TimeSeries, TimeSeriesDatasetBase } from '@/graphql/types/dimensions';
import { useTranslation } from '@/hooks/useTranslation';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface HistoricalInformationProps {
  variableInfo: {
    title: string;
    description: string;
    period?: string;
    source: string;
    width?: string;
    titleWidth?: string;
    titleMinWidth?: string;
    valueWidth?: string;
  };
  timeSeries?: TimeSeries<TimeSeriesDatasetBase>;
  onCloseModal?: () => void;
}

export const HistoricalInformation = ({
  variableInfo,
  timeSeries,
  onCloseModal,
}: HistoricalInformationProps) => {
  const [value, setValue] = useState(0);
  const { t } = useTranslation();

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Previne a propagação do evento de clique para não fechar o tooltip
  const handleContainerClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  // Transform time series data for the chart
  const getChartData = () => {
    if (!timeSeries || !timeSeries.labels || !timeSeries.datasets[0]) {
      return [];
    }

    const dataset = timeSeries.datasets[0];
    return timeSeries.labels.map((label, index) => ({
      name: label,
      value: dataset.data[index] || 0,
      trend: dataset.trend_data?.[index],
    }));
  };

  const chartData = getChartData();
  const hasHistoricalData = timeSeries && timeSeries.labels && timeSeries.datasets?.length > 0;

  // Reset tab to 0 if there's no historical data and user is on tab 1
  useEffect(() => {
    if (!hasHistoricalData && value === 1) {
      setValue(0);
    }
  }, [hasHistoricalData, value]);

  return (
    <HistoricalInformationContainer
      variant="outlined"
      onClick={handleContainerClick}
      onMouseDown={handleContainerClick}
    >
      <HistoricalInformationHeader>
        <Text variant="subtitle1" color="text.primary">
          {variableInfo.title}
        </Text>

        <Mobile>
          <IconButton onClick={onCloseModal} sx={{ padding: 0 }}>
            <Close color="action" />
          </IconButton>
        </Mobile>
      </HistoricalInformationHeader>

      {hasHistoricalData && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <StyledTabs value={value} onChange={handleChange}>
            <StyledTab
              label={t('tabs.tabs.historical_information.variable_information')}
              {...a11yProps(0)}
            />
            <StyledTab
              label={t('tabs.tabs.historical_information.historical_data')}
              {...a11yProps(1)}
            />
          </StyledTabs>
        </Box>
      )}

      <CustomTabPanel value={value} index={0}>
        <HistoricalInfo
          description={variableInfo.description}
          period={variableInfo.period}
          source={variableInfo.source}
          width={variableInfo.width}
          titleWidth={variableInfo.titleWidth}
          titleMinWidth={variableInfo.titleMinWidth}
          valueWidth={variableInfo.valueWidth}
        />
      </CustomTabPanel>

      {hasHistoricalData && (
        <CustomTabPanel value={value} index={1}>
          <LineChart data={chartData} />
        </CustomTabPanel>
      )}
    </HistoricalInformationContainer>
  );
};
